import { Alert, Box, CssBaseline, Snackbar } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import LayoutShell from "../../../components/layout/layoutShell";
import { useCurrentProfile } from "../../../ContextWrapper";
import { useDialogManager } from "../../../ui_hooks/useDialogManager";
import { useHideGlobalNav } from "./shared/useHideGlobalNav";
import ServerDataTableAdapter from "./table/adapters/ServerDataTableAdapter";

const theme = createTheme({
  typography: {
    fontFamily:
      'Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  },
  palette: {
    primary: { main: "#1976d2" },
    secondary: { main: "#dc004e" },
  },
});

export default function ManagementScreen({
  definition,
  sectionConfigs,
  sectionProviderResolver,
  cogMenuItems,
  experience,
}) {
  const { section } = useParams();
  const navigate = useNavigate();
  const { openDialog, data, open: openDialogManager, closeDialog } = useDialogManager();
  const { profile } = useCurrentProfile();
  const resolvedExperience = experience || "mvp";

  const canGoBack = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.history.length > 1;
  }, []);

  useHideGlobalNav();

  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: null });
  const [selectedRows, setSelectedRows] = useState([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const isSectionAllowed = definition.tabs.some((tab) => tab.value === section);

  useEffect(() => {
    if (!section || !isSectionAllowed) {
      navigate(`/management/${definition.defaultSection}`, { replace: true });
    }
  }, [definition.defaultSection, isSectionAllowed, navigate, section]);

  useEffect(() => {
    setSelectedRows([]);
  }, [section]);

  const handleTabChange = useCallback(
    (event, newTab) => {
      setSortConfig({ field: null, direction: null });
      setSelectedRows([]);
      navigate(`/management/${newTab}`);
    },
    [navigate]
  );

  const handleRowsPerPageChange = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
  }, []);

  const handleSortChange = useCallback((field, direction) => {
    setSortConfig({ field, direction });
  }, []);

  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const config = sectionConfigs[section];
  const DialogComponent = config?.dialogs;
  const SectionProvider = useMemo(
    () => sectionProviderResolver(section),
    [section, sectionProviderResolver]
  );

  if (!isSectionAllowed) {
    return null;
  }

  if (!config || !SectionProvider) {
    return <div>Section not found</div>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <SectionProvider
        profile={profile}
        experience={resolvedExperience}
        columns={config.columns}
        rowsPerPage={rowsPerPage}
        sortConfig={sortConfig}
        onSortChange={handleSortChange}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        openDialogManager={openDialogManager}
        notify={setSnackbar}
        navigate={navigate}
      >
        {(sectionController) => (
          <>
            <Box
              data-management-experience={resolvedExperience}
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "white",
                zIndex: 2000,
                overflow: "auto",
              }}
            >
              <LayoutShell
                title={config.title}
                tabs={definition.tabs}
                activeTab={section}
                onTabChange={handleTabChange}
                cogMenuItems={cogMenuItems}
                onCreateClick={sectionController.toolbar.createAction}
                onSearchChange={sectionController.toolbar.onSearchChange}
                searchValue={sectionController.toolbar.searchValue || ""}
                searchContext={section || "unknown"}
                createButtonText={config.createButtonText}
                showBackButton={resolvedExperience !== "platform" && canGoBack}
                onBackClick={() => navigate(-1)}
                showCreateButton={Boolean(
                  sectionController.toolbar.createAction
                )}
              >
                {typeof sectionController.toolbar.renderTop === "function"
                  ? sectionController.toolbar.renderTop()
                  : null}

                {sectionController.tableProps.hideTable ? (
                  <Box>
                    {typeof sectionController.tableProps
                      .renderAlternateContent === "function"
                      ? sectionController.tableProps.renderAlternateContent()
                      : null}
                  </Box>
                ) : (
                  <ServerDataTableAdapter
                    columns={sectionController.tableProps.columns}
                    rows={sectionController.tableProps.rows}
                    onDeleteRequest={
                      sectionController.tableProps.onDeleteRequest
                    }
                    emptyMessage={sectionController.tableProps.emptyMessage}
                    loading={sectionController.tableProps.loading}
                    error={sectionController.tableProps.error}
                    searchTerm={sectionController.tableProps.searchTerm}
                    page={sectionController.tableProps.page}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    hasNextPage={sectionController.tableProps.hasNextPage}
                    hasPrevPage={sectionController.tableProps.hasPrevPage}
                    onNextPage={sectionController.tableProps.onNextPage}
                    onPreviousPage={sectionController.tableProps.onPreviousPage}
                    onFiltersChange={
                      sectionController.tableProps.onFiltersChange
                    }
                    onSortChange={handleSortChange}
                    sortConfig={sortConfig}
                    selectedRows={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                    renderCellContent={
                      sectionController.tableProps.renderCellContent
                    }
                    renderRowActionsMenu={
                      sectionController.tableProps.renderRowActionsMenu
                    }
                    renderFilterChips={
                      sectionController.tableProps.renderFilterChips
                    }
                    filterPopoverByField={
                      sectionController.tableProps.filterPopoverByField
                    }
                    renderSelectionToolbar={
                      sectionController.tableProps.renderSelectionToolbar ||
                      sectionController.toolbar.renderSelection
                    }
                  />
                )}
              </LayoutShell>
            </Box>

            {sectionController?.dialogs?.render
              ? sectionController.dialogs.render({ openDialog, data, closeDialog })
              : null}

            {DialogComponent ? (
              <DialogComponent
                openDialog={openDialog}
                data={data}
                closeDialog={closeDialog}
                experience={resolvedExperience}
                onSubmit={sectionController.commands.refresh}
                onDelete={sectionController.commands.deleteRow}
                onDeleteRequest={sectionController.tableProps.onDeleteRequest}
                {...(sectionController.dialogs.componentProps || {})}
                onShowSuccess={(message, severity = "success", autoHideDuration) =>
                  setSnackbar({
                    open: true,
                    message,
                    severity,
                    autoHideDuration,
                  })
                }
              />
            ) : null}
          </>
        )}
      </SectionProvider>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={
          snackbar.autoHideDuration !== undefined ? snackbar.autoHideDuration : 5000
        }
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: 99999 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor: snackbar.severity === "error" ? "#ef4444" : "#627CFB",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
